/** Geometry helpers. Coordinates are GeoJSON order: [lng, lat] (+ optional elevation). */
export type LngLat = [number, number]
export type LngLatEle = [number, number, number]

const R = 6371000 // earth radius, meters
const rad = (d: number) => (d * Math.PI) / 180
const deg = (r: number) => (r * 180) / Math.PI

export function haversineMeters(a: LngLat | LngLatEle, b: LngLat | LngLatEle): number {
  const dLat = rad(b[1] - a[1])
  const dLng = rad(b[0] - a[0])
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

export function lineDistanceMeters(coords: readonly (LngLat | LngLatEle)[]): number {
  let d = 0
  for (let i = 1; i < coords.length; i++) d += haversineMeters(coords[i - 1], coords[i])
  return d
}

/** Distance from the line start to each vertex, in meters. */
export function cumulativeMeters(coords: readonly (LngLat | LngLatEle)[]): number[] {
  const out = new Array<number>(coords.length)
  out[0] = 0
  for (let i = 1; i < coords.length; i++) out[i] = out[i - 1] + haversineMeters(coords[i - 1], coords[i])
  return out
}

/** Initial bearing from a to b in degrees, 0..360 (0 = north). */
export function bearingDeg(a: LngLat | LngLatEle, b: LngLat | LngLatEle): number {
  const φ1 = rad(a[1])
  const φ2 = rad(b[1])
  const Δλ = rad(b[0] - a[0])
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return (deg(Math.atan2(y, x)) + 360) % 360
}

/** Point at the given distance and bearing from the origin. */
export function destination(origin: LngLat, distM: number, bearing: number): LngLat {
  const φ1 = rad(origin[1])
  const λ1 = rad(origin[0])
  const θ = rad(bearing)
  const δ = distM / R
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ))
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2))
  return [((deg(λ2) + 540) % 360) - 180, deg(φ2)]
}

export interface NearestOnLine {
  point: LngLat
  /** index of the segment start vertex */
  index: number
  /** distance from the query point to the line, meters */
  distM: number
  /** distance along the line from its start, meters */
  alongM: number
}

/** Nearest point on a polyline, using a local equirectangular projection (fine for <100km lines). */
export function nearestPointOnLine(coords: readonly (LngLat | LngLatEle)[], p: LngLat): NearestOnLine {
  const cosLat = Math.cos(rad(p[1]))
  const px = p[0] * cosLat
  const py = p[1]
  let best: NearestOnLine = { point: [coords[0][0], coords[0][1]], index: 0, distM: Infinity, alongM: 0 }
  let along = 0
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i]
    const b = coords[i + 1]
    const ax = a[0] * cosLat
    const ay = a[1]
    const bx = b[0] * cosLat
    const by = b[1]
    const dx = bx - ax
    const dy = by - ay
    const lenSq = dx * dx + dy * dy
    let t = lenSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lenSq
    t = Math.max(0, Math.min(1, t))
    const cand: LngLat = [(ax + t * dx) / cosLat, ay + t * dy]
    const d = haversineMeters(cand, p)
    const segLen = haversineMeters(a, b)
    if (d < best.distM) best = { point: cand, index: i, distM: d, alongM: along + t * segLen }
    along += segLen
  }
  return best
}

/** Point at the given distance along a polyline. */
export function alongLine(coords: readonly (LngLat | LngLatEle)[], distM: number): LngLat {
  if (distM <= 0) return [coords[0][0], coords[0][1]]
  let acc = 0
  for (let i = 1; i < coords.length; i++) {
    const seg = haversineMeters(coords[i - 1], coords[i])
    if (acc + seg >= distM) {
      const t = seg === 0 ? 0 : (distM - acc) / seg
      return [
        coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
        coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
      ]
    }
    acc += seg
  }
  const last = coords[coords.length - 1]
  return [last[0], last[1]]
}

/** Douglas–Peucker simplification with a tolerance in meters. Keeps elevation of kept points. */
export function simplify<T extends LngLat | LngLatEle>(coords: readonly T[], toleranceM: number): T[] {
  if (coords.length <= 2) return [...coords]
  const cosLat = Math.cos(rad(coords[0][1]))
  const mPerDeg = 111320
  const x = (c: T) => c[0] * cosLat * mPerDeg
  const y = (c: T) => c[1] * mPerDeg
  const keep = new Uint8Array(coords.length)
  keep[0] = keep[coords.length - 1] = 1
  const stack: [number, number][] = [[0, coords.length - 1]]
  while (stack.length) {
    const [s, e] = stack.pop()!
    const ax = x(coords[s])
    const ay = y(coords[s])
    const bx = x(coords[e])
    const by = y(coords[e])
    const dx = bx - ax
    const dy = by - ay
    const lenSq = dx * dx + dy * dy
    let maxD = 0
    let maxI = -1
    for (let i = s + 1; i < e; i++) {
      const pxi = x(coords[i])
      const pyi = y(coords[i])
      let d: number
      if (lenSq === 0) d = Math.hypot(pxi - ax, pyi - ay)
      else {
        const t = Math.max(0, Math.min(1, ((pxi - ax) * dx + (pyi - ay) * dy) / lenSq))
        d = Math.hypot(pxi - (ax + t * dx), pyi - (ay + t * dy))
      }
      if (d > maxD) {
        maxD = d
        maxI = i
      }
    }
    if (maxD > toleranceM && maxI > 0) {
      keep[maxI] = 1
      stack.push([s, maxI], [maxI, e])
    }
  }
  return coords.filter((_, i) => keep[i] === 1)
}

/** Total ascent/descent from an elevation series, ignoring noise below the threshold. */
export function ascentDescent(elevations: readonly number[], thresholdM = 2): { ascent: number; descent: number } {
  let ascent = 0
  let descent = 0
  let ref = elevations.length ? elevations[0] : 0
  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - ref
    if (diff >= thresholdM) {
      ascent += diff
      ref = elevations[i]
    } else if (diff <= -thresholdM) {
      descent -= diff
      ref = elevations[i]
    }
  }
  return { ascent: Math.round(ascent), descent: Math.round(descent) }
}

export function boundsOf(coords: readonly (LngLat | LngLatEle)[]): [LngLat, LngLat] {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const c of coords) {
    if (c[0] < minX) minX = c[0]
    if (c[0] > maxX) maxX = c[0]
    if (c[1] < minY) minY = c[1]
    if (c[1] > maxY) maxY = c[1]
  }
  return [
    [minX, minY],
    [maxX, maxY],
  ]
}

export const formatKm = (m: number) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`)

export const formatDuration = (sec: number) => {
  const h = Math.floor(sec / 3600)
  const m = Math.round((sec % 3600) / 60)
  return h > 0 ? `${h}:${String(m).padStart(2, '0')} h` : `${m} min`
}

export const formatSpeed = (mPerS: number) => `${(mPerS * 3.6).toFixed(1)} km/h`
